import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, btnPrimary } from "@/lib/constants";
import { toast } from "sonner";
import { Save, RotateCcw, Eye, Loader2, Code } from "lucide-react";

export const FormatEditor = ({ title, desc, endpoint, placeholders, testId, showLetterheadAssets = false }) => {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [logo, setLogo] = useState("");
  const [sig, setSig] = useState("");
  const [savingAssets, setSavingAssets] = useState(false);

  useEffect(() => {
    api.get(endpoint).then((r) => setHtml(r.data.html)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
    if (showLetterheadAssets) {
      api.get("/settings/letterhead-assets").then((r) => { setLogo(r.data.logo_data || ""); setSig(r.data.signature_data || ""); }).catch(() => {});
    }
  }, [endpoint, showLetterheadAssets]);

  const save = async () => {
    setSaving(true);
    try { await api.put(endpoint, { html }); toast.success("Format saved"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Reset this template to default?")) return;
    try { const r = await api.post(`${endpoint}/reset`); setHtml(r.data.html); toast.success("Reset to default"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const pickImage = (setter) => (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 500 * 1024) { toast.error("Image too large — keep it under 500 KB for a clean PDF"); return; }
    const reader = new FileReader();
    reader.onload = () => setter(reader.result);
    reader.readAsDataURL(f);
    e.target.value = "";
  };
  const saveAssets = async () => {
    setSavingAssets(true);
    try { await api.put("/settings/letterhead-assets", { logo_data: logo, signature_data: sig }); toast.success("Letterhead assets saved"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSavingAssets(false); }
  };

  const insert = (ph) => setHtml((h) => h + `{{${ph}}}`);
  const swapStubImages = (h) => {
    if (!h) return h;
    const logoStubs = /^(logo\.(png|jpe?g|svg)|letterhead-logo\.png|watermark\.png)$/i;
    const sigStubs = /^(signature\.(png|jpe?g)|sign\.(png|jpe?g))$/i;
    return h.replace(/<img\b[^>]*\bsrc\s*=\s*"([^"]*)"[^>]*>/gi, (tag, src) => {
      const file = (src || "").split("/").pop();
      let target = null;
      if (logoStubs.test(file) && logo) target = logo;
      else if (sigStubs.test(file) && sig) target = sig;
      if (!target) return tag;
      return tag.replace(/src\s*=\s*"[^"]*"/i, `src="${target}"`);
    });
  };
  const previewHtml = swapStubImages(html)
    .replace(/\{\{items_rows\}\}/g, '<tr><td>Processing Fee</td><td align="center">1</td><td align="right">5,000.00</td><td align="center">18</td><td align="right">5,900.00</td></tr>')
    .replace(/\{\{logo_url\}\}/g, logo || "")
    .replace(/\{\{signature_url\}\}/g, sig || "")
    .replace(/\{\{(\w+)\}\}/g, (_, k) => `<span style="background:#d1fae5;padding:0 3px;border-radius:3px">${k}</span>`);

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-emerald-600" size={28} /></div>;

  return (
    <div className="space-y-5" data-testid={testId}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">{title}</h1>
          <p className="text-sm text-slate-500 mt-1">{desc}</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="format-preview-toggle" onClick={() => setPreview((p) => !p)} className="flex items-center gap-2 px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium border border-slate-200">{preview ? <Code size={15} /> : <Eye size={15} />} {preview ? "Edit HTML" : "Preview"}</button>
          <button data-testid="format-reset" onClick={reset} className="flex items-center gap-2 px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium border border-slate-200"><RotateCcw size={15} /> Reset</button>
          <button data-testid="format-save" onClick={save} disabled={saving} className={btnPrimary}>{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save</button>
        </div>
      </div>

      {showLetterheadAssets && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="letterhead-assets">
          <div className="text-sm font-semibold text-slate-700">Letterhead Assets</div>
          <p className="text-xs text-slate-500">Upload the logo and authorised signature — use them in the template via <span className="font-mono bg-slate-100 px-1">{"{{logo_url}}"}</span> and <span className="font-mono bg-slate-100 px-1">{"{{signature_url}}"}</span> inside <code>&lt;img src=…&gt;</code>. Shared across both Proposal & Undertaking.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-200 p-3 space-y-2">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Logo</div>
              <div className="h-24 flex items-center justify-center bg-slate-50 rounded border border-dashed border-slate-200 overflow-hidden">
                {logo ? <img src={logo} alt="logo" className="max-h-20" data-testid="letterhead-logo-preview" /> : <span className="text-xs text-slate-400">No logo uploaded</span>}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer inline-flex items-center gap-1">Choose image <input data-testid="letterhead-logo-input" type="file" accept="image/*" className="hidden" onChange={pickImage(setLogo)} /></label>
                {logo && <button data-testid="letterhead-logo-remove" onClick={() => setLogo("")} className="text-xs text-rose-600 hover:underline">Remove</button>}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3 space-y-2">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Signature</div>
              <div className="h-24 flex items-center justify-center bg-slate-50 rounded border border-dashed border-slate-200 overflow-hidden">
                {sig ? <img src={sig} alt="signature" className="max-h-20" data-testid="letterhead-sig-preview" /> : <span className="text-xs text-slate-400">No signature uploaded</span>}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 cursor-pointer inline-flex items-center gap-1">Choose image <input data-testid="letterhead-sig-input" type="file" accept="image/*" className="hidden" onChange={pickImage(setSig)} /></label>
                {sig && <button data-testid="letterhead-sig-remove" onClick={() => setSig("")} className="text-xs text-rose-600 hover:underline">Remove</button>}
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <button data-testid="letterhead-save" onClick={saveAssets} disabled={savingAssets} className={btnPrimary}>{savingAssets ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Letterhead</button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <span className="text-xs text-slate-500 self-center">Insert placeholder:</span>
        {placeholders.map((ph) => (
          <button key={ph} data-testid={`ph-${ph}`} onClick={() => insert(ph)} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-emerald-100 text-slate-700 border border-slate-200 font-mono">{`{{${ph}}}`}</button>
        ))}
      </div>

      {preview ? (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 overflow-auto" data-testid="format-preview" dangerouslySetInnerHTML={{ __html: previewHtml }} />
      ) : (
        <textarea data-testid="format-html" value={html} onChange={(e) => setHtml(e.target.value)} rows={22} className={`${inputCls} font-mono text-xs leading-relaxed`} spellCheck={false} />
      )}
    </div>
  );
};
